import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useCacheBuster } from 'react-cache-buster';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
// mui
import { ThemeProvider } from '@mui/material';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import CssBaseline from '@mui/material/CssBaseline';
// common
import Router from './common/route/Router';
import { SFToastContainer } from '@/component/common/sf-toast-container';
// asset & font
import theme from '@/theme';
import { Global, css } from '@emotion/react';
import fontsStyle from '@/assets/fonts/fonts';
// provider
import { CacheProvider } from '@emotion/react';
import createEmotionCache from '@/createEmotionCache';
// lib
import { SessionRefresh } from './lib/web3Auth/SessionRefresh';
// etc
import packageJson from '../package.json'; // '../package.json';
import 'react-toastify/dist/ReactToastify.css';

// import './index.css';
import { RecoilRoot } from 'recoil';
import useStakingEffects from './hooks/use-addActions';
import { webCheck } from './webVersion';

import { Web3AuthProvider } from '@web3auth/modal/react';
import web3AuthContextConfig from './contexts/web3authContext';
import { WagmiProvider } from '@web3auth/modal/react/wagmi';

import { Web3Provider } from './contexts/web3Provider';
import { ContractPoolProvider } from './contexts/contract-pool-context';
import { readVersion } from './api/staking/read';

const emotionCache = createEmotionCache();
const queryClient = new QueryClient();

export default function App() {
  const version = packageJson.version;
  const { checkCacheStatus } = useCacheBuster();
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;

  useStakingEffects(location);

  // 버전 체크 함수
  const checkVersion = async () => {
    try {
      // 이미 새로고침했는지 확인 (무한 루프 방지)
      const hasReloaded = sessionStorage.getItem('version-check-reloaded');
      if (hasReloaded === 'true') {
        return;
      }

      const clientVersion = packageJson.version;
      const serverResponse = await readVersion();
      const serverVersion = serverResponse?.data.version;

      if (!serverVersion) {
        console.log('Server version not found');
        return;
      }
      // 버전이 일치하지 않으면 새로고침
      if (serverVersion && serverVersion !== clientVersion) {
        sessionStorage.setItem('version-check-reloaded', 'true');
        window.location.reload();
      }
    } catch {
      // 버전 체크 실패는 무시
    }
  };

  // 초기 버전 체크
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      checkVersion();
    }
  }, []);

  // 화면이 보일 때 버전 체크 (visibilitychange 이벤트)
  useEffect(() => {
    const handleVisibilityChange = () => {
      // 탭이 보이게 될 때만 체크
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // 윈도우 포커스를 받을 때 버전 체크
  useEffect(() => {
    const handleFocus = () => {
      if (process.env.NODE_ENV === 'production') {
        checkVersion();
      }
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  useEffect(() => {
    const isWeb = webCheck(packageJson.version);
    if (isWeb === false) {
      window.location.reload();
      navigate('/');
    }
    return () => {};
  }, []);

  useEffect(() => {
    console.log(`version: ${version}`);
    checkCacheStatus();

    return () => {};
  }, [checkCacheStatus]);

  // 사용자의 액션행위가 컨트랙트에는 반영이 됐는데 서버에 실패할 경우
  // 세션스토리지에서 확인 후 있으면 적용 뒤 해당 세션을 삭제한다.
  // useEffect(() => {
  //   (async () => {
  //     await stakings();
  //     await unstakings();
  //     await rewards();
  //     await withdraws();
  //   })();

  //   return () => {};
  // }, [location]);
  // const stakings = async () => {
  //   const re = localGetStorage('stake');
  //   if (re?.length > 0) {
  //     await stakingUsers(re[0]);
  //     removeFirstLocalStorage('stake');
  //   }
  // };
  // const unstakings = async () => {
  //   const re = localGetStorage('unStake');
  //   if (re?.length > 0) {
  //     await unstakingUsers(re[0]);
  //     removeFirstLocalStorage('unStake');
  //   }
  // };

  // const rewards = async () => {
  //   const re = localGetStorage('reward');
  //   if (re?.length > 0) {
  //     await claimRewardUsers(re[0]);
  //     removeFirstLocalStorage('reward');
  //   }
  // };

  // const withdraws = async () => {
  //   const re = localGetStorage('withdraw');
  //   if (re?.length > 0) {
  //     await unstakingUsers(re[0]);
  //     removeFirstLocalStorage('withdraw');
  //   }
  // };

  // ----- 새로고침 막기 변수 -----
  // const preventClose = (e: BeforeUnloadEvent) => {
  //   e.preventDefault();
  //   e.returnValue = ''; // chrome에서는 설정이 필요해서 넣은 코드
  // };

  // // 브라우저에 렌더링 시 한 번만 실행하는 코드
  // useEffect(() => {
  //   (() => {
  //     window.addEventListener('beforeunload', preventClose);
  //   })();

  //   return () => {
  //     window.removeEventListener('beforeunload', preventClose);
  //   };
  // }, []);
  // // ----- 새로고침 막기 변수 end -----

  // const userLang = navigator.language;
  // const lang = changeLang(userLang);
  // const langPayload = {
  //   key: 'lang',
  //   value: { lang },
  // };
  // setStorage(langPayload);

  // const type = RouterList().find((item: any) => item.url === path);
  // if (type?.url) {
  //   const beForeUrl = urlSessionStorage().url;
  //   let url = beForeUrl !== '/signIn' ? beForeUrl : type.url;
  //   const payload = {
  //     key: 'url',
  //     value: { url },
  //   };
  //   setStorage(payload);
  // }

  // if (process.env.NODE_ENV === 'production') {
  //   let device = null;
  //   if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
  //     // 모바일인 경우
  //     device = 'mobile';
  //   } else {
  //     device = 'other';
  //   }
  //   ReactGA.set({ hitType: device, page: path });
  //   ReactGA.pageview(path);
  // }

  // if (projectId === undefined) {
  //   alert('project ID undefined');
  //   return <>error project ID</>;
  // }

  return (
    <div className="App">
      <SFToastContainer
        closeButton={false}
        hideProgressBar
        position="top-center"
        style={{
          paddingLeft: '24px',
          paddingRight: '24px',
          top: '50px',
        }}
        icon={
          <CheckCircleOutlineRoundedIcon
            sx={{
              fontSize: '16px',
            }}
          />
        }
      />
      <Web3AuthProvider config={web3AuthContextConfig}>
        <Web3Provider>
          <ContractPoolProvider>
            <QueryClientProvider client={queryClient}>
              <WagmiProvider>
                <CacheProvider value={emotionCache}>
                  <ThemeProvider theme={theme}>
                    <RecoilRoot>
                      <CssBaseline />
                      <SessionRefresh>
                        <Router path={path} />
                      </SessionRefresh>
                    </RecoilRoot>
                  </ThemeProvider>
                  <Global
                    styles={css`
                      ${fontsStyle}
                    `}
                  />
                </CacheProvider>
              </WagmiProvider>
            </QueryClientProvider>
          </ContractPoolProvider>
        </Web3Provider>
      </Web3AuthProvider>
    </div>
  );
}
